const INTEREST_KEYWORDS = {
    'Web Development': ['web', 'fullstack', 'frontend', 'backend', 'react', 'javascript', 'php', 'python', 'node'],
    'Design': ['design', 'graphic', 'ui', 'ux', 'visual', 'branding'],
    'Data Analysis': ['data', 'analytics', 'science', 'engineering', 'sql'],
    'Marketing': ['marketing', 'digital market', 'product marketing', 'advertising'],
    'Programming': ['programming', 'software', 'development', 'coding', 'engineer'],
    'Security': ['security', 'cyber', 'ethical', 'penetration'],
    'AI': ['ai', 'machine learning', 'automation', 'artificial', 'ml'],
    'Blockchain': ['blockchain', 'crypto', 'web3'],
};

const GOAL_SYNONYMS = [
    { terms: ['web', 'website', 'developer', 'frontend', 'backend', 'fullstack', 'programmer', 'software'], keywords: ['web', 'fullstack', 'frontend', 'backend', 'software', 'development'] },
    { terms: ['design', 'designer', 'creative', 'ui', 'ux', 'graphic'], keywords: ['design', 'graphic', 'ui', 'ux'] },
    { terms: ['data', 'analyst', 'analytics', 'scientist'], keywords: ['data', 'analytics', 'science'] },
    { terms: ['market', 'marketing', 'sales', 'brand'], keywords: ['market', 'marketing'] },
    { terms: ['security', 'cyber', 'hack'], keywords: ['security', 'cyber'] },
    { terms: ['ai', 'machine learning', 'automation'], keywords: ['ai', 'machine', 'automation'] },
    { terms: ['cloud', 'devops', 'infrastructure'], keywords: ['cloud', 'devops'] },
    { terms: ['mobile', 'app', 'android', 'ios'], keywords: ['mobile', 'app'] },
    { terms: ['business', 'entrepreneur', 'startup'], keywords: ['business', 'entre', 'management'] },
];

const BUDGET_RANGES = {
    'under-75k': { min: 0, max: 75000 },
    '75k-150k': { min: 75000, max: 150000 },
    '150k-250k': { min: 150000, max: 250000 },
    '250k+': { min: 250000, max: Infinity },
};

const BEGINNER_COURSES = ['fundamental', 'digital literacy', 'basic', 'introduction'];

const ADVANCED_COURSES = ['advanced', 'fullstack', 'engineering', 'devops', 'cloud', 'data science'];

function getCourseText(course) {
    return `${course.name} ${course.description} ${(course.curriculum || []).join(' ')}`.toLowerCase();
}

function scoreInterest(courseText, interests) {
    let score = 0;
    const reasons = [];

    interests.forEach(interest => {
        const keywords = INTEREST_KEYWORDS[interest] || [interest.toLowerCase()];
        const hits = keywords.filter(kw => courseText.includes(kw));
        if (hits.length > 0) {
            score += 3 + hits.length;
            reasons.push(interest);
        }
    });

    return { score, reasons };
}

function scoreGoals(courseText, goals) {
    const goalsLower = (goals || '').toLowerCase();
    let score = 0;
    const reasons = [];

    GOAL_SYNONYMS.forEach(({ terms, keywords }) => {
        if (terms.some(term => goalsLower.includes(term))) {
            const hits = keywords.filter(kw => courseText.includes(kw));
            if (hits.length > 0) {
                score += 2 + hits.length;
                reasons.push('career goals');
            }
        }
    });

    return { score, reasons };
}

function scoreBudget(coursePrice, budget) {
    const range = BUDGET_RANGES[budget];
    if (!range) return { score: 0, reasons: [] };

    if (coursePrice >= range.min && coursePrice <= range.max) {
        return { score: 4, reasons: ['within your budget'] };
    }
    if (coursePrice <= range.max * 1.15) {
        return { score: 1, reasons: ['slightly above budget'] };
    }
    return { score: -2, reasons: [] };
}

function scoreExperience(courseText, experience) {
    if (!experience) return { score: 0, reasons: [] };

    const isBeginnerCourse = BEGINNER_COURSES.some(kw => courseText.includes(kw));
    const isAdvancedCourse = ADVANCED_COURSES.some(kw => courseText.includes(kw));

    if (experience === 'beginner' && isBeginnerCourse) {
        return { score: 3, reasons: ['beginner-friendly'] };
    }
    if (experience === 'beginner' && isAdvancedCourse) {
        return { score: -1, reasons: [] };
    }
    if (experience === 'advanced' && isAdvancedCourse) {
        return { score: 3, reasons: ['matches your experience level'] };
    }
    if (experience === 'intermediate') {
        return { score: 1, reasons: [] };
    }
    return { score: 0, reasons: [] };
}

function scoreTimeCommitment(duration, timeCommitment) {
    if (!duration || !timeCommitment) return { score: 0, reasons: [] };

    const weeks = parseInt(duration, 10) || 12;

    if (timeCommitment === '5-10' && weeks <= 8) {
        return { score: 2, reasons: ['fits your schedule'] };
    }
    if (timeCommitment === '10-20' && weeks <= 12) {
        return { score: 2, reasons: ['fits your schedule'] };
    }
    if (timeCommitment === '20+') {
        return { score: 1, reasons: [] };
    }
    return { score: 0, reasons: [] };
}

function buildMatchReason(uniqueReasons) {
    if (uniqueReasons.length === 0) return 'Popular at Abia Tech Hub';
    const display = [...new Set(uniqueReasons)].slice(0, 2);
    return `Matched: ${display.join(', ')}`;
}

export function getBudgetOptions(courses) {
    const prices = courses.map(c => c.price).filter(Boolean);
    const maxPrice = Math.max(...prices);

    return [
        { value: 'under-75k', label: `Under ₦75,000` },
        { value: '75k-150k', label: `₦75,000 – ₦150,000` },
        { value: '150k-250k', label: `₦150,000 – ₦250,000` },
        { value: '250k+', label: `₦250,000+ (up to ₦${maxPrice.toLocaleString()})` },
    ];
}

export function recommendCourses(courses, formData, limit = 3) {
    const scored = courses.map(course => {
        const courseText = getCourseText(course);
        const allReasons = [];

        const interest = scoreInterest(courseText, formData.interests || []);
        const goals = scoreGoals(courseText, formData.goals);
        const budget = scoreBudget(course.price, formData.budget);
        const experience = scoreExperience(courseText, formData.experience);
        const time = scoreTimeCommitment(course.duration, formData.timeCommitment);

        const totalScore = interest.score + goals.score + budget.score + experience.score + time.score;
        allReasons.push(...interest.reasons, ...goals.reasons, ...budget.reasons, ...experience.reasons, ...time.reasons);

        return {
            ...course,
            matchScore: totalScore,
            matchReason: buildMatchReason(allReasons),
            isFallback: false,
        };
    });

    const positiveMatches = scored
        .filter(c => c.matchScore > 0)
        .sort((a, b) => b.matchScore - a.matchScore);

    if (positiveMatches.length > 0) {
        return positiveMatches.slice(0, limit);
    }

    // Fallback: budget-aware popular picks, then affordable starters
    const fallback = [...scored]
        .map(c => ({
            ...c,
            isFallback: true,
            matchReason: formData.experience === 'beginner'
                ? 'Recommended starter course'
                : 'Popular course at Abia Tech Hub',
        }))
        .sort((a, b) => {
            const budgetRange = BUDGET_RANGES[formData.budget];
            if (budgetRange) {
                const aInBudget = a.price >= budgetRange.min && a.price <= budgetRange.max;
                const bInBudget = b.price >= budgetRange.min && b.price <= budgetRange.max;
                if (aInBudget !== bInBudget) return bInBudget - aInBudget;
            }
            return a.price - b.price;
        });

    return fallback.slice(0, limit);
}

export function buildLeadPayload(formData, topRecommendation) {
    const nameParts = (formData.name || '').trim().split(/\s+/);
    const interests = (formData.interests || []).join(', ');
    const topCourse = topRecommendation?.name || 'Pending match';

    return {
        firstname: nameParts[0] || 'N/A',
        lastname: nameParts.slice(1).join(' ') || 'N/A',
        phoneno: formData.phone || 'N/A',
        email: formData.email || 'N/A',
        residence: 'Zuriel Course Advisor',
        course: topCourse,
        interests,
        experience: formData.experience || '',
        budget: formData.budget || '',
        time_commitment: formData.timeCommitment || '',
        goals: (formData.goals || '').slice(0, 500),
    };
}

export function isValidPhone(phone) {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 14;
}
